import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Logo } from '../components/Logo';
import {
  Mic, MicOff, Play, Pause, Square, Settings, Lightbulb, LogOut, Search,
  Save, Copy, Edit2, Trash2, Plus, ExternalLink, HelpCircle, X, Volume2,
  Briefcase, User, Folder, ChevronRight, Loader2, Check, Share2, Link, Clock, Eye,
  Headphones, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  briefcase: Briefcase,
  user: User,
  lightbulb: Lightbulb,
  folder: Folder,
};

// Modal component using Portal to avoid DOM reconciliation issues
const Modal = ({ isOpen, children }) => {
  if (!isOpen) return null;
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      {children}
    </div>,
    document.body
  );
};

// Função para mostrar notificação usando DOM manipulation direta (fora do React)
const showToast = (message, type = 'success') => {
  // Remover toast anterior se existir
  const existingToast = document.getElementById('app-toast');
  if (existingToast) {
    existingToast.remove();
  }
  
  // Criar novo elemento de toast
  const toast = document.createElement('div');
  toast.id = 'app-toast';
  toast.className = `fixed top-4 right-4 z-[9999] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-white ${
    type === 'error' ? 'bg-red-500' : 'bg-green-500'
  }`;
  toast.innerHTML = `
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      ${type === 'error' 
        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>'
        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>'
      }
    </svg>
    <span>${message}</span>
  `;
  
  document.body.appendChild(toast);
  
  // Remover após 3 segundos
  setTimeout(() => {
    if (toast.parentNode) {
      toast.remove();
    }
  }, 3000);
};

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  // Usar a função showToast em vez de estado React
  const showNotification = useCallback((message, type = 'success') => {
    showToast(message, type);
  }, []);
  
  // Text & Voice State
  const [text, setText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isPausedRecording, setIsPausedRecording] = useState(false);
  const [savedTextBeforePause, setSavedTextBeforePause] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  
  // Voice Settings - Load from localStorage or use defaults
  const [voiceSettings, setVoiceSettings] = useState(() => {
    const saved = localStorage.getItem('voiceSettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { language: 'pt-BR', gender: 'any', rate: 1 };
      }
    }
    return { language: 'pt-BR', gender: 'any', rate: 1 };
  });
  const [tempVoiceSettings, setTempVoiceSettings] = useState(voiceSettings);
  const [showSettings, setShowSettings] = useState(false);

  // Save voice settings to localStorage
  const saveVoiceSettings = () => {
    setVoiceSettings(tempVoiceSettings);
    localStorage.setItem('voiceSettings', JSON.stringify(tempVoiceSettings));
    showNotification('Configurações de voz salvas!');
    setShowSettings(false);
  };

  // Reset temp settings when opening modal
  const openVoiceSettings = () => {
    setTempVoiceSettings(voiceSettings);
    setShowSettings(true);
  };
  
  // Ideas State
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Save Modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [ideaTitle, setIdeaTitle] = useState('');
  const [ideaCategory, setIdeaCategory] = useState('');
  const [editingIdea, setEditingIdea] = useState(null);
  const [savingIdea, setSavingIdea] = useState(false);
  
  // Sort & Tutorial
  const [sortOrder, setSortOrder] = useState('newest');
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialContent, setTutorialContent] = useState('');
  
  // Share Modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingIdea, setSharingIdea] = useState(null);
  const [shareExpiration, setShareExpiration] = useState(24);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  
  // Support
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [supportEmail, setSupportEmail] = useState(null);
  
  // Delete All Ideas
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deletingAllIdeas, setDeletingAllIdeas] = useState(false);
  
  // Refs
  const recognitionRef = useRef(null);
  const wordsRef = useRef([]);

  // Check user status
  useEffect(() => {
    if (user) {
      if (user.status === 'pending') navigate('/aguardando');
      else if (user.status === 'blocked') navigate('/bloqueado');
      else if (user.status === 'expired') navigate('/trial-expirado');
    }
  }, [user, navigate]);

  // Fetch data
  useEffect(() => {
    if (token && (user?.status === 'active' || user?.status === 'trial')) {
      fetchCategories();
      fetchIdeas();
      fetchAnnouncements();
      fetchTutorial();
      // Tutorial removido do carregamento automático - agora só aparece quando o usuário clica no botão
    }
  }, [token, user]);

  // Fetch support email
  useEffect(() => {
    const fetchSupportEmail = async () => {
      try {
        const response = await axios.get(`${API}/support/config`);
        setSupportEmail(response.data.support_email);
      } catch (error) {
        console.log('Support config not available');
      }
    };
    fetchSupportEmail();
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = voiceSettings.language;
      recognition.maxAlternatives = 1;
      
      recognitionRef.current = recognition;
    } else {
      console.warn('Speech Recognition not supported');
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);

  // Update language when settings change
  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang = voiceSettings.language;
    }
  }, [voiceSettings.language]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchIdeas = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category_id', selectedCategory);
      
      const response = await axios.get(`${API}/ideas?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdeas(response.data);
    } catch (error) {
      console.error('Error fetching ideas:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const fetchTutorial = async () => {
    try {
      const response = await axios.get(`${API}/tutorial`);
      setTutorialContent(response.data.content);
    } catch (error) {
      console.error('Error fetching tutorial:', error);
    }
  };

  useEffect(() => {
    if (token && (user?.status === 'active' || user?.status === 'trial')) {
      const debounce = setTimeout(() => fetchIdeas(), 300);
      return () => clearTimeout(debounce);
    }
  }, [searchQuery, selectedCategory]);

  // Voice Recording Functions
  const startRecording = useCallback(() => {
    if (!recognitionRef.current) {
      showNotification('Reconhecimento de voz não suportado neste navegador', 'error');
      return;
    }

    const recognition = recognitionRef.current;
    
    // Salvar texto atual antes de começar (para concatenar depois)
    const currentText = text;
    const wasPaused = isPausedRecording;
    const textBeforeRecording = wasPaused ? savedTextBeforePause : currentText;
    
    // Set up handlers each time we start
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        // Concatenar com texto salvo antes da pausa
        if (textBeforeRecording) {
          setText(textBeforeRecording + ' ' + transcript);
        } else {
          setText(transcript);
        }
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        showNotification('Permissão do microfone negada. Clique no cadeado na barra de endereços e permita o microfone.', 'error');
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        showNotification(`Erro: ${event.error}`, 'error');
      }
      setIsRecording(false);
      setIsPausedRecording(false);
    };
    
    recognition.onend = () => {
      // Verificar via ref se ainda está gravando para evitar problemas de estado
      // Não fazer nada aqui - o auto-restart será controlado manualmente
    };

    try {
      recognition.lang = voiceSettings.language;
      recognition.start();
      
      // Atualizar estados
      setIsRecording(true);
      setIsPausedRecording(false);
      
      if (wasPaused) {
        showNotification('Gravação retomada - continue falando!');
      } else {
        setSavedTextBeforePause(currentText);
        showNotification('Gravação iniciada - fale agora!');
      }
    } catch (error) {
      console.error('Error starting recognition:', error);
      if (error.message?.includes('already started')) {
        setIsRecording(true);
      } else {
        showNotification('Erro ao iniciar gravação. Recarregue a página.', 'error');
      }
    }
  }, [text, isPausedRecording, savedTextBeforePause, voiceSettings.language, showNotification]);

  const pauseRecording = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
    // Salvar o texto atual para continuar depois
    setSavedTextBeforePause(text);
    setIsRecording(false);
    setIsPausedRecording(true);
    showNotification('Gravação pausada - clique para continuar');
  }, [text, showNotification]);

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore
      }
    }
    setIsRecording(false);
    setIsPausedRecording(false);
    setSavedTextBeforePause('');
    showNotification('Gravação finalizada');
  }, [showNotification]);

  // Text-to-Speech Functions
  const getVoices = useCallback(() => {
    const voices = speechSynthesis.getVoices();
    let filteredVoices = voices.filter(v => v.lang.startsWith(voiceSettings.language.split('-')[0]));
    
    if (voiceSettings.gender !== 'any') {
      const genderKeywords = voiceSettings.gender === 'female' 
        ? ['female', 'woman', 'feminino', 'mujer', 'femme']
        : ['male', 'man', 'masculino', 'hombre', 'homme'];
      
      const genderFiltered = filteredVoices.filter(v => 
        genderKeywords.some(k => v.name.toLowerCase().includes(k))
      );
      
      if (genderFiltered.length > 0) filteredVoices = genderFiltered;
    }
    
    return filteredVoices.length > 0 ? filteredVoices[0] : voices[0];
  }, [voiceSettings]);

  const playText = () => {
    if (!text.trim()) {
      showNotification('Digite ou grave um texto primeiro', 'error');
      return;
    }

    if (isPaused) {
      speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
      return;
    }

    speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = getVoices();
    utterance.rate = voiceSettings.rate;
    utterance.lang = voiceSettings.language;
    
    wordsRef.current = text.split(/\s+/);
    let wordIndex = 0;
    
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        setCurrentWordIndex(wordIndex);
        wordIndex++;
        setPlaybackProgress((wordIndex / wordsRef.current.length) * 100);
      }
    };
    
    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      setPlaybackProgress(100);
      setCurrentWordIndex(-1);
      setTimeout(() => setPlaybackProgress(0), 1000);
    };
    
    utterance.onerror = () => {
      setIsPlaying(false);
      setIsPaused(false);
      showNotification('Erro ao reproduzir áudio', 'error');
    };
    
    speechSynthesis.speak(utterance);
    setIsPlaying(true);
    setIsPaused(false);
  };

  const pausePlayback = () => {
    speechSynthesis.pause();
    setIsPaused(true);
    setIsPlaying(false);
  };

  const stopPlayback = () => {
    speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setPlaybackProgress(0);
    setCurrentWordIndex(-1);
  };

  // Idea Management
  const editingIdeaRef = useRef(null);
  
  const openSaveModal = (idea = null) => {
    if (idea) {
      setEditingIdea(idea);
      editingIdeaRef.current = idea;  // Guardar referência também
      setIdeaTitle(idea.title);
      setIdeaCategory(idea.category_id || '');
      setText(idea.content);
    } else {
      setEditingIdea(null);
      editingIdeaRef.current = null;
      setIdeaTitle('');
      setIdeaCategory('');
    }
    setShowSaveModal(true);
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    // Não limpar o editingIdea aqui para preservar o estado caso o usuário reabra
  };

  const saveIdea = async () => {
    if (!ideaTitle.trim()) {
      showNotification('Digite um título para a ideia', 'error');
      return;
    }
    if (!text.trim()) {
      showNotification('Digite ou grave o conteúdo da ideia', 'error');
      return;
    }

    // Usar a ref para garantir que temos o valor mais atual
    const currentEditingIdea = editingIdeaRef.current;
    const isUpdating = currentEditingIdea && currentEditingIdea.id;

    setSavingIdea(true);
    try {
      const data = {
        title: ideaTitle,
        content: text,
        category_id: ideaCategory || null
      };

      if (isUpdating) {
        // ATUALIZAR ideia existente
        await axios.put(`${API}/ideas/${currentEditingIdea.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // CRIAR nova ideia
        await axios.post(`${API}/ideas`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Limpar estados de forma síncrona
      setSavingIdea(false);
      setShowSaveModal(false);
      setEditingIdea(null);
      editingIdeaRef.current = null;
      
      // Buscar ideias primeiro (sem notificação)
      try {
        const res = await axios.get(`${API}/ideas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIdeas(res.data);
      } catch (e) {
        console.error('Error fetching ideas:', e);
      }
      
      // Mostrar notificação depois que tudo estiver atualizado
      showNotification(isUpdating ? 'Ideia atualizada!' : 'Ideia salva!');
      
    } catch (error) {
      setSavingIdea(false);
      showNotification(error.response?.data?.detail || 'Erro ao salvar ideia', 'error');
    }
  };

  const loadIdea = (idea) => {
    setText(idea.content);
    showNotification('Ideia carregada');
  };

  const copyIdea = async (content) => {
    await navigator.clipboard.writeText(content);
    showNotification('Texto copiado!');
  };

  const deleteIdea = async (ideaId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ideia?')) return;
    
    try {
      await axios.delete(`${API}/ideas/${ideaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar lista primeiro
      try {
        const res = await axios.get(`${API}/ideas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIdeas(res.data);
      } catch (e) {
        console.error('Error fetching ideas:', e);
      }
      
      // Depois notificar
      showNotification('Ideia excluída');
    } catch (error) {
      showNotification('Erro ao excluir ideia', 'error');
    }
  };

  const deleteAllIdeas = async () => {
    setDeletingAllIdeas(true);
    try {
      const response = await axios.delete(`${API}/ideas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowDeleteAllModal(false);
      setDeletingAllIdeas(false);
      
      // Atualizar lista (será vazia)
      setIdeas([]);
      
      // Depois notificar
      showNotification(response.data.message || 'Todas as ideias foram excluídas');
    } catch (error) {
      setDeletingAllIdeas(false);
      showNotification('Erro ao excluir ideias', 'error');
    }
  };

  // Delete Account
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const deleteMyAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Conta excluída com sucesso');
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (error) {
      showNotification('Erro ao excluir conta', 'error');
      setDeletingAccount(false);
    }
  };

  // Share Functions
  const openShareModal = (idea) => {
    setSharingIdea(idea);
    setShareExpiration(24);
    setShareResult(null);
    setShowShareModal(true);
  };

  const createShareLink = async () => {
    if (!sharingIdea) return;
    
    setShareLoading(true);
    try {
      const response = await axios.post(`${API}/ideas/${sharingIdea.id}/share`, {
        idea_id: sharingIdea.id,
        expires_in_hours: shareExpiration
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const shareUrl = `${window.location.origin}/compartilhado/${response.data.share_id}`;
      setShareResult({
        url: shareUrl,
        expires_at: response.data.expires_at,
        expires_in_hours: response.data.expires_in_hours
      });
      showNotification('Link criado com sucesso!');
    } catch (error) {
      showNotification('Erro ao criar link', 'error');
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = async () => {
    if (shareResult?.url) {
      await navigator.clipboard.writeText(shareResult.url);
      showNotification('Link copiado!');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Render highlighted text
  const renderHighlightedText = () => {
    if (!text) return null;
    
    // Always return consistent React elements to avoid React reconciliation issues
    const words = text.split(/(\s+)/);
    let wordCount = 0;
    
    return words.map((word, index) => {
      if (word.trim()) {
        const isHighlighted = isPlaying && wordCount === currentWordIndex;
        wordCount++;
        return (
          <span key={index} className={isHighlighted ? 'bg-orange-200 rounded px-1 transition-colors' : 'transition-colors'}>
            {word}
          </span>
        );
      }
      return <span key={index}>{word}</span>;
    });
  };

  const getCategoryIcon = (iconName) => iconMap[iconName] || Folder;

  if (!user || (user.status !== 'active' && user.status !== 'trial')) return null;

  const sortedIdeas = [...ideas].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      {/* Notificações agora são gerenciadas fora do React via showToast */}

      {/* Trial Banner */}
      {user.status === 'trial' && user.trial_days_left !== undefined && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <span className="text-sm font-medium">
              ⏰ Período de teste: {user.trial_days_left} {user.trial_days_left === 1 ? 'dia restante' : 'dias restantes'}
            </span>
            <a href="/pagamento" className="text-xs underline hover:no-underline ml-2">
              Assinar agora
            </a>
          </div>
        </div>
      )}
      
      {/* Announcements Banner */}
      {announcements.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-semibold">{announcements[0].title}</span>
              <span className="text-white/80">{announcements[0].content}</span>
            </div>
            {announcements[0].link && (
              <a href={announcements[0].link} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm font-medium hover:underline">
                Saiba mais <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="sm" showText={true} />
            <span className="text-xs text-slate-500 ml-2">Olá, {user.name}</span>
          </div>
          
          <div className="flex items-center gap-2">
            {user.status === 'trial' && (
              <a 
                href="/pagamento" 
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-medium rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all shadow-sm"
                data-testid="subscribe-btn"
              >
                Assinar Agora
              </a>
            )}
            <button onClick={() => setShowSupportModal(true)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="support-btn" title="Suporte">
              <Headphones className="w-5 h-5 text-slate-600" />
            </button>
            <button onClick={() => setShowTutorial(true)} className="px-3 py-2 hover:bg-orange-50 rounded-lg flex items-center gap-1.5 border border-orange-200 bg-orange-50" data-testid="tutorial-btn">
              <HelpCircle className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-orange-600">Tutorial</span>
            </button>
            <button onClick={() => setShowDeleteAccountModal(true)} className="p-2 hover:bg-red-50 rounded-lg" data-testid="delete-account-btn" title="Excluir minha conta">
              <Trash2 className="w-5 h-5 text-red-500" />
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="logout-btn">
              <LogOut className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Text Area Card */}
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Área de Texto</h2>
                <button onClick={openVoiceSettings} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50 flex items-center gap-2" data-testid="voice-settings-btn">
                  <Settings className="w-4 h-4" />
                  Configurações
                </button>
              </div>

              {/* Text Input */}
              {isPlaying ? (
                <div className="min-h-[200px] p-4 bg-slate-100 rounded-lg text-base leading-relaxed whitespace-pre-wrap">
                  {renderHighlightedText()}
                </div>
              ) : (
                <textarea
                  placeholder="Digite sua ideia aqui ou clique no microfone para gravar..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="w-full min-h-[200px] p-3 text-base resize-none border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="text-input"
                />
              )}

              {/* Recording Controls */}
              <div className="flex flex-col items-center gap-2 py-4">
                <div className="flex items-center gap-4">
                  {/* Botão principal de gravação */}
                  <button
                    onClick={isPausedRecording ? startRecording : (isRecording ? pauseRecording : startRecording)}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                      isRecording 
                        ? 'bg-amber-500 hover:bg-amber-600' 
                        : isPausedRecording
                          ? 'bg-green-500 hover:bg-green-600 animate-pulse'
                          : 'bg-orange-500 hover:bg-orange-600'
                    } text-white shadow-lg`}
                    data-testid="record-btn"
                    title={isRecording ? 'Pausar gravação' : isPausedRecording ? 'Continuar gravação' : 'Iniciar gravação'}
                  >
                    {isRecording ? (
                      <Pause className="w-6 h-6" />
                    ) : isPausedRecording ? (
                      <Mic className="w-6 h-6" />
                    ) : (
                      <Mic className="w-6 h-6" />
                    )}
                  </button>
                  
                  {/* Botão de parar (só aparece quando gravando ou pausado) */}
                  {(isRecording || isPausedRecording) && (
                    <button
                      onClick={stopRecording}
                      className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 text-white shadow-lg transition-all"
                      data-testid="stop-record-btn"
                      title="Parar e finalizar gravação"
                    >
                      <Square className="w-5 h-5" />
                    </button>
                  )}
                  
                  {isRecording && (
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-1 bg-amber-500 rounded-full animate-pulse" style={{ height: `${8 + Math.random() * 16}px`, animationDelay: `${i * 0.1}s` }} />
                      ))}
                    </div>
                  )}
                </div>
                
                {isRecording && (
                  <p className="text-sm text-amber-600 font-medium animate-pulse">
                    Gravando... clique para pausar
                  </p>
                )}
                {isPausedRecording && (
                  <p className="text-sm text-green-600 font-medium animate-pulse">
                    Pausado - clique para continuar
                  </p>
                )}
              </div>

              {/* Playback Controls */}
              <div className="space-y-3">
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 transition-all duration-200"
                    style={{ width: `${playbackProgress}%` }}
                  />
                </div>
                
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={isPlaying ? pausePlayback : playText}
                    disabled={!text.trim()}
                    className="p-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="play-pause-btn"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={stopPlayback}
                    disabled={!isPlaying && !isPaused}
                    className="p-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="stop-btn"
                  >
                    <Square className="w-4 h-4" />
                  </button>
                  <div className="w-px h-8 bg-slate-200" />
                  <button
                    onClick={() => openSaveModal()}
                    disabled={!text.trim()}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    data-testid="save-idea-btn"
                  >
                    <Save className="w-4 h-4" />
                    Salvar Ideia
                  </button>
                  <button
                    onClick={() => copyIdea(text)}
                    disabled={!text.trim()}
                    className="p-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="copy-text-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Idea Bank */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-orange-500" />
                  <h2 className="text-lg font-semibold text-slate-900">Banco de Ideias</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-700 rounded-md">{ideas.length}</span>
                  {ideas.length > 0 && (
                    <button
                      onClick={() => setShowDeleteAllModal(true)}
                      className="px-2 py-1 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-1"
                      data-testid="delete-all-ideas-btn"
                      title="Apagar todas as ideias"
                    >
                      <Trash2 className="w-3 h-3" />
                      Apagar Todos
                    </button>
                  )}
                  <button
                    onClick={() => { setText(''); openSaveModal(); }}
                    className="px-2 py-1 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-1"
                    data-testid="new-idea-btn"
                  >
                    <Plus className="w-3 h-3" />
                    Nova
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar ideias..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 pl-10 pr-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="search-ideas-input"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-2">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="flex-1 h-10 px-3 border rounded-lg bg-white text-sm"
                  data-testid="category-filter"
                >
                  <option value="all">Todas categorias</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="h-10 px-3 border rounded-lg bg-white text-sm"
                  data-testid="sort-order"
                >
                  <option value="newest">Mais recentes</option>
                  <option value="oldest">Mais antigas</option>
                </select>
              </div>

              {/* Ideas List */}
              <div className="space-y-3 max-h-[400px] overflow-auto">
                {sortedIdeas.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhuma ideia salva ainda</p>
                    <p className="text-sm">Grave ou digite sua primeira ideia!</p>
                  </div>
                ) : (
                  sortedIdeas.map((idea) => {
                    const IconComponent = getCategoryIcon(categories.find(c => c.id === idea.category_id)?.icon);
                    return (
                      <div key={idea.id} className="p-4 border rounded-xl hover:border-orange-300 transition-colors" data-testid={`idea-card-${idea.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-medium text-slate-900 line-clamp-1">{idea.title}</h4>
                          {idea.category_name && (
                            <span className="text-xs px-2 py-0.5 rounded-full border flex items-center gap-1" style={{ borderColor: categories.find(c => c.id === idea.category_id)?.color }}>
                              <IconComponent className="w-3 h-3" />
                              {idea.category_name}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2 mb-3">{idea.content}</p>
                        <div className="flex items-center gap-1">
                          <button onClick={() => loadIdea(idea)} className="px-2 py-1 text-xs hover:bg-slate-100 rounded flex items-center gap-1">
                            <ChevronRight className="w-3 h-3" /> Carregar
                          </button>
                          <button onClick={() => copyIdea(idea.content)} className="p-1.5 hover:bg-slate-100 rounded">
                            <Copy className="w-3 h-3" />
                          </button>
                          <button onClick={() => openShareModal(idea)} className="p-1.5 hover:bg-slate-100 rounded text-orange-500" data-testid={`share-idea-${idea.id}`}>
                            <Share2 className="w-3 h-3" />
                          </button>
                          <button onClick={() => openSaveModal(idea)} className="p-1.5 hover:bg-slate-100 rounded">
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button onClick={() => deleteIdea(idea.id)} className="p-1.5 hover:bg-red-50 rounded text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Voice Settings Modal */}
      <Modal isOpen={showSettings}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-lg">Configurações de Voz</h3>
            </div>
            <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Idioma</label>
              <select
                value={tempVoiceSettings.language}
                onChange={(e) => setTempVoiceSettings(s => ({ ...s, language: e.target.value }))}
                className="w-full h-10 px-3 border rounded-lg"
                data-testid="voice-language-select"
              >
                <option value="pt-BR">Português (Brasil)</option>
                <option value="en-US">English (US)</option>
                <option value="es-ES">Español</option>
                <option value="fr-FR">Français</option>
                <option value="de-DE">Deutsch</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Gênero da Voz</label>
              <select
                value={tempVoiceSettings.gender}
                onChange={(e) => setTempVoiceSettings(s => ({ ...s, gender: e.target.value }))}
                className="w-full h-10 px-3 border rounded-lg"
                data-testid="voice-gender-select"
              >
                <option value="any">Qualquer</option>
                <option value="female">Feminina</option>
                <option value="male">Masculina</option>
              </select>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <label className="text-sm font-medium">Velocidade</label>
                <span className="text-sm text-slate-500">{tempVoiceSettings.rate}x</span>
              </div>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={tempVoiceSettings.rate}
                onChange={(e) => setTempVoiceSettings(s => ({ ...s, rate: parseFloat(e.target.value) }))}
                className="w-full"
                data-testid="voice-speed-slider"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 border-t">
            <button 
              onClick={() => setShowSettings(false)} 
              className="px-4 py-2 border rounded-lg hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button 
              onClick={saveVoiceSettings} 
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
              data-testid="save-voice-settings-btn"
            >
              <Check className="w-4 h-4" />
              Salvar
            </button>
          </div>
        </div>
      </Modal>

      {/* Save Idea Modal */}
      <Modal isOpen={showSaveModal}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold text-lg">{editingIdea ? 'Editar Ideia' : 'Salvar Ideia'}</h3>
            <button onClick={closeSaveModal} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Título</label>
              <input
                type="text"
                placeholder="Ex: Ideia para o projeto X"
                value={ideaTitle}
                onChange={(e) => setIdeaTitle(e.target.value)}
                className="w-full h-10 px-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                data-testid="idea-title-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Categoria</label>
              <select
                value={ideaCategory}
                onChange={(e) => setIdeaCategory(e.target.value)}
                className="w-full h-10 px-3 border rounded-lg"
                data-testid="idea-category-select"
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Conteúdo</label>
              <textarea
                placeholder="Digite ou grave sua ideia..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full min-h-[100px] p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                data-testid="idea-content-input"
              />
              <div className="flex items-center gap-2 flex-wrap">
                {!isRecording && !isPausedRecording && (
                  <button
                    onClick={startRecording}
                    className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 border hover:bg-slate-50"
                    data-testid="modal-record-btn"
                  >
                    <Mic className="w-4 h-4" /> Gravar
                  </button>
                )}
                {isRecording && (
                  <button
                    onClick={pauseRecording}
                    className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 bg-amber-500 text-white"
                    data-testid="modal-pause-btn"
                  >
                    <Pause className="w-4 h-4" /> Pausar
                  </button>
                )}
                {isPausedRecording && (
                  <button
                    onClick={startRecording}
                    className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 bg-green-500 text-white"
                    data-testid="modal-continue-btn"
                  >
                    <Mic className="w-4 h-4" /> Continuar
                  </button>
                )}
                {(isRecording || isPausedRecording) && (
                  <button
                    onClick={stopRecording}
                    className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 bg-red-500 text-white"
                    data-testid="modal-stop-btn"
                  >
                    <Square className="w-4 h-4" /> Parar
                  </button>
                )}
                {isRecording && (
                  <span className="text-xs text-amber-600 animate-pulse">Gravando...</span>
                )}
                {isPausedRecording && (
                  <span className="text-xs text-green-600">Pausado</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 p-4 border-t">
            <button onClick={closeSaveModal} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
              Cancelar
            </button>
            <button
              onClick={saveIdea}
              disabled={savingIdea || !text.trim()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
              data-testid="confirm-save-idea-btn"
            >
              {savingIdea ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingIdea ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Tutorial Modal */}
      <Modal isOpen={showTutorial}>
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-lg">Como usar a plataforma</h3>
            </div>
            <button onClick={() => setShowTutorial(false)} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <div className="tutorial-content">
              {tutorialContent ? (
                <div dangerouslySetInnerHTML={{ 
                  __html: tutorialContent
                    // Headers
                    .replace(/^### (.+)$/gm, '<h4 class="text-base font-semibold text-slate-800 mt-4 mb-2">$1</h4>')
                    .replace(/^## (.+)$/gm, '<h3 class="text-lg font-bold text-orange-600 mt-5 mb-2 flex items-center gap-2">$1</h3>')
                    // Bold
                    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
                    // Horizontal rule
                    .replace(/^---$/gm, '<hr class="my-4 border-slate-200"/>')
                    // List items
                    .replace(/^- (.+)$/gm, '<li class="ml-4 mb-1 text-slate-600">• $1</li>')
                    // Line breaks
                    .replace(/\n/g, '<br/>')
                }} />
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-orange-600 mb-2">🎙️ Gravação de Voz</h3>
                    <p className="text-slate-600">Clique no botão do microfone para iniciar a gravação. Use o botão de pausa para interromper temporariamente.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-orange-600 mb-2">✍️ Digitação</h3>
                    <p className="text-slate-600">Digite ou cole textos na área de texto principal.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-orange-600 mb-2">🔊 Reprodução</h3>
                    <p className="text-slate-600">Clique em play para ouvir seu texto. Use as configurações para ajustar idioma e velocidade.</p>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-orange-600 mb-2">💡 Banco de Ideias</h3>
                    <p className="text-slate-600">Salve suas ideias com título e categoria. Use a busca e filtros para encontrar rapidamente.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="p-4 border-t">
            <button onClick={() => setShowTutorial(false)} className="w-full py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
              Entendi!
            </button>
          </div>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal isOpen={showShareModal}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-orange-500" />
              <h3 className="font-semibold text-lg">Compartilhar Ideia</h3>
            </div>
            <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {!shareResult ? (
            <>
              <div className="p-4 space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{sharingIdea?.title}</p>
                  <p className="text-sm text-slate-500 line-clamp-2">{sharingIdea?.content}</p>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    Tempo de expiração
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setShareExpiration(24)}
                      className={`p-3 border rounded-lg text-center transition-colors ${
                        shareExpiration === 24 
                          ? 'border-orange-500 bg-orange-50 text-orange-600' 
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid="expire-24h"
                    >
                      <span className="font-medium">24 horas</span>
                      <p className="text-xs text-slate-500">Link temporário</p>
                    </button>
                    <button
                      onClick={() => setShareExpiration(168)}
                      className={`p-3 border rounded-lg text-center transition-colors ${
                        shareExpiration === 168 
                          ? 'border-orange-500 bg-orange-50 text-orange-600' 
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid="expire-7d"
                    >
                      <span className="font-medium">7 dias</span>
                      <p className="text-xs text-slate-500">Mais tempo</p>
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 p-4 border-t">
                <button onClick={() => setShowShareModal(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
                  Cancelar
                </button>
                <button
                  onClick={createShareLink}
                  disabled={shareLoading}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                  data-testid="create-share-link-btn"
                >
                  {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
                  Criar Link
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 space-y-4">
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="font-medium text-slate-900">Link criado!</p>
                  <p className="text-sm text-slate-500">
                    Expira em {shareResult.expires_in_hours === 24 ? '24 horas' : '7 dias'}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                  <input
                    type="text"
                    value={shareResult.url}
                    readOnly
                    className="flex-1 bg-transparent text-sm outline-none"
                    data-testid="share-link-input"
                  />
                  <button
                    onClick={copyShareLink}
                    className="p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                    data-testid="copy-share-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                
                <p className="text-xs text-slate-500 text-center">
                  Qualquer pessoa com este link poderá ver e ouvir sua ideia
                </p>
              </div>
              
              <div className="p-4 border-t">
                <button 
                  onClick={() => setShowShareModal(false)} 
                  className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Account Modal */}
      <Modal isOpen={showDeleteAccountModal}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Excluir minha conta?</h3>
            <p className="text-slate-500 mb-6">
              Esta ação é irreversível. Todas as suas ideias e dados serão excluídos permanentemente.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAccountModal(false)}
                disabled={deletingAccount}
                className="flex-1 py-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={deleteMyAccount}
                disabled={deletingAccount}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Sim, excluir'
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Support Modal */}
      <Modal isOpen={showSupportModal}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Headphones className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Suporte</h3>
            
            {supportEmail ? (
              <>
                <p className="text-slate-500 mb-4">
                  Precisa de ajuda? Entre em contato conosco pelo email:
                </p>
                <div className="bg-slate-100 rounded-lg p-4 mb-4">
                  <p className="text-lg font-semibold text-slate-900 flex items-center justify-center gap-2">
                    <Mail className="w-5 h-5 text-orange-500" />
                    {supportEmail}
                  </p>
                </div>
                <a
                  href={`mailto:${supportEmail}?subject=Suporte Voice Ideias`}
                  className="inline-block w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                >
                  Enviar Email
                </a>
              </>
            ) : (
              <p className="text-slate-500 mb-4">
                O suporte está temporariamente indisponível. Por favor, tente novamente mais tarde.
              </p>
            )}
            
            <button
              onClick={() => setShowSupportModal(false)}
              className="mt-4 w-full py-2 border rounded-lg hover:bg-slate-50 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete All Ideas Modal */}
      <Modal isOpen={showDeleteAllModal}>
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Apagar todas as ideias?</h3>
            <p className="text-slate-500 mb-2">
              Você está prestes a excluir <span className="font-bold text-red-500">{ideas.length} {ideas.length === 1 ? 'ideia' : 'ideias'}</span>.
            </p>
            <p className="text-slate-500 mb-6 text-sm">
              Esta ação é irreversível. Todas as suas ideias e links compartilhados serão excluídos permanentemente.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAllModal(false)}
                disabled={deletingAllIdeas}
                className="flex-1 py-2 border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={deleteAllIdeas}
                disabled={deletingAllIdeas}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-delete-all-btn"
              >
                {deletingAllIdeas ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  'Sim, apagar todas'
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
